import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { addFuelSurcharge, editFuelSurcharge } from '../api';
import {
  AddFuelInitialValidation,
  AddFuelInitialValues,
  AddFuelTrueValidation,
} from '../../../initial-values';
import { AddFuelSurchargeDto } from '../../../types';
import { isValidationValidated } from '../../../utils/helper';
import SelectCarrierDropdown from '../../../components/global-components/selectCarrierDropdown';
import calendarIcon from '../../../assets/icons/new_icon/calendar.svg';
import X from '../../../assets/icons/new_icon/x.svg';
import DateRangePicker from '../../../components/global-components/litePicker';

interface Props {
  onClose: any;
  onSuccess: any;
  editData?: any;
}

export default function AddFuelSurchargeModal({
  onClose,
  onSuccess,
  editData,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [formData, setFormData] = useState<AddFuelSurchargeDto>(
    editData ? editData : AddFuelInitialValues
  );
  const [formValidations, setFormValidations] = useState(
    editData ? AddFuelTrueValidation : AddFuelInitialValidation
  );
  const [selectedDates, setSelectedDates] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  }>({
    startDate: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000),
    endDate: new Date(),
  });

  function handleAction() {
    setValidating(true);
    if (isValidationValidated(formValidations)) {
      setLoading(true);
      if (editData) {
        editFuelSurcharge(formData)
          .then((response) => {
            if (!response.data.is_error) {
              enqueueSnackbar('Fuel Surcharge has been updated.', {
                variant: 'success',
              });
              onSuccess();
              setLoading(false);
            }
          })
          .catch((e) => {
            setLoading(false);
          });
      } else {
        addFuelSurcharge(formData)
          .then((response) => {
            if (!response.data.is_error) {
              enqueueSnackbar('Fuel Surcharge has been added.', {
                variant: 'success',
              });
              onSuccess();
              setLoading(false);
            }
          })
          .catch((e) => {
            setLoading(false);
          });
      }
    }
  }

  const handleClose = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleDateChange = (startDate: any, endDate: any) => {
    setSelectedDates({ startDate, endDate });
    const startDateInstance = startDate.dateInstance;
    const endDateInstance = endDate.dateInstance;
    const endDateObject = new Date(endDateInstance);
    const startDateObject = new Date(startDateInstance);

    // Check if the dateInstances are valid dates
    if (!isNaN(startDateObject.getTime()) && !isNaN(endDateObject.getTime())) {
      const updatedDate =
        startDateObject.getFullYear() +
        '-' +
        ('0' + (startDateObject.getMonth() + 1)).slice(-2) +
        '-' +
        ('0' + startDateObject.getDate()).slice(-2) +
        ':' +
        endDateObject.getFullYear() +
        '-' +
        ('0' + (endDateObject.getMonth() + 1)).slice(-2) +
        '-' +
        ('0' + endDateObject.getDate()).slice(-2);

      // Update formData with the selected date range
      setFormData({
        ...formData,
        updated_date: updatedDate,
      });

      // Update formValidations based on whether startDate and endDate are not null
      setFormValidations({
        ...formValidations,
        updated_date: startDate !== null && endDate !== null,
      });
    }
  };
  return (
    <div
      className='modal'
      id='fsc_modal'
      style={{ display: 'block', backgroundColor: 'rgba(0, 0, 0, 0.5) ' }}
      tabIndex={-1}
      aria-labelledby='fsc_modal_label'
      aria-hidden='true'
      onClick={handleClose}
    >
      {loading && (
        <div className='loader-container'>
          <div className='spinner'></div>
        </div>
      )}
      <div className='modal-dialog modal-md'>
        <div className='modal-content'>
          <div className='modal-header d-flex w-100 justify-content-between px-3 py-2'>
            <h3 className='newubbemodalheading  mb-0'> Fuel Surcharge</h3>
            <img
              alt='X'
              src={X}
              onClick={() => onClose()}
              style={{ cursor: 'pointer' }}
            />
          </div>

          <div className='modal-body'>
            <div className='mb-3'>
              <label htmlFor='carrier' className=' globalInputLabel'>
                Carrier
              </label>
              <span className='text-danger'>*</span>

              <SelectCarrierDropdown
                selectedCarrierCode={formData?.carrier_code}
                className={`form-control p-0 ${
                  validating &&
                  (formData.carrier_code === null ||
                    formData.carrier_code === undefined)
                    ? ''
                    : ''
                }`}
                setSelectedCarrierCode={(val: any) => {
                  setFormData({
                    ...formData,
                    carrier_code: val,
                  });
                  setFormValidations({
                    ...formValidations,
                    carrier_code: val !== null || val !== undefined,
                  });
                }}
              />
              {validating &&
                (formData.carrier_code === null ||
                  formData.carrier_code === undefined) && (
                  <div className='error_text'>
                    {'Please select a valid carrier.'}
                  </div>
                )}
            </div>
            <div className='mb-3'>
              <label htmlFor='fuel_type' className=' globalInputLabel'>
                Fuel Type
              </label>
              <span className='text-danger'>*</span>

              <select
                className={`form-control form form-select   ubbe-input ${
                  formData.fuel_type ? 'highlighted-border' : ''
                }
                ${
                  validating && formData.fuel_type !== ''
                    ? ''
                    : validating && formData.fuel_type === ''
                    ? 'is-invalid'
                    : ''
                }
                `}
                id='fuel_type'
                aria-label='Fuel Type'
                value={formData.fuel_type}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    fuel_type: e.target.value,
                  });
                }}
              >
                <option value='D'>Domestic</option>
                <option value='I'>International</option>
              </select>
              <p className='text-danger' id='fuel_type_error'></p>
            </div>
            <div className='mb-3'>
              <label htmlFor='ten_thou_under' className=' globalInputLabel'>
                0 to 9,000
              </label>
              <span className='text-danger'>*</span>

              <input
                type='text'
                className={`form-control  ubbe-input  ${
                  validating && formData.ten_thou_under !== ''
                    ? ''
                    : validating && formData.ten_thou_under === ''
                    ? 'is-invalid'
                    : ''
                } ${formData.ten_thou_under ? 'highlighted-border' : ''}`}
                id='ten_thou_under'
                placeholder='0 to 9,000'
                autoComplete='new-password'
                value={formData.ten_thou_under}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    ten_thou_under: e.target.value,
                  });
                  setFormValidations({
                    ...formValidations,
                    ten_thou_under: e.target.value !== '',
                  });
                }}
              />
              {validating && formData.ten_thou_under === '' && (
                <div className='error_text'>
                  {'Please provide a valid value.'}
                </div>
              )}
            </div>
            <div className='mb-3'>
              <label
                htmlFor='ten_thou_to_fifty_five_thou'
                className=' globalInputLabel'
              >
                10,000 to 54,999 <span className='text-danger'>*</span>
              </label>
              <input
                type='text'
                className={`form-control  ubbe-input  ${
                  validating && formData.ten_thou_to_fifty_five_thou !== ''
                    ? ''
                    : validating && formData.ten_thou_to_fifty_five_thou === ''
                    ? 'is-invalid'
                    : ''
                } ${
                  formData.ten_thou_to_fifty_five_thou
                    ? 'highlighted-border'
                    : ''
                }`}
                id='ten_thou_to_fifty_five_thou'
                placeholder='10,000 to 54,999'
                autoComplete='new-password'
                value={formData.ten_thou_to_fifty_five_thou}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    ten_thou_to_fifty_five_thou: e.target.value,
                  });
                  setFormValidations({
                    ...formValidations,
                    ten_thou_to_fifty_five_thou: e.target.value !== '',
                  });
                }}
              />
              {validating && formData.ten_thou_to_fifty_five_thou === '' && (
                <div className='error_text'>
                  {'Please provide a valid value.'}
                </div>
              )}
            </div>
            <div className='mb-3'>
              <label
                htmlFor='fifty_five_thou_greater'
                className=' globalInputLabel'
              >
                55,000 and Greater <span className='text-danger'>*</span>
              </label>
              <input
                type='text'
                className={`form-control  ubbe-input  ${
                  validating && formData.fifty_five_thou_greater !== ''
                    ? ''
                    : validating && formData.fifty_five_thou_greater === ''
                    ? 'is-invalid'
                    : ''
                } ${
                  formData.fifty_five_thou_greater ? 'highlighted-border' : ''
                }`}
                id='fifty_five_thou_greater'
                placeholder='55,000 and Greater'
                autoComplete='new-password'
                value={formData.fifty_five_thou_greater}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    fifty_five_thou_greater: e.target.value,
                  });
                  setFormValidations({
                    ...formValidations,
                    fifty_five_thou_greater: e.target.value !== '',
                  });
                }}
              />
              {validating && formData.fifty_five_thou_greater === '' && (
                <div className='error_text'>
                  {'Please provide a valid value.'}
                </div>
              )}
            </div>
            <div className='mb-3'>
              <label htmlFor='updated_date' className=' globalInputLabel'>
                Updated Date <span className='text-danger'>*</span>
              </label>

              <DateRangePicker
                id={'date_range_picker'}
                className={'form-control ubbe-input date-range-picker'}
                selectedDates={selectedDates}
                onChange={handleDateChange}
                calendarIcon={calendarIcon}
              />

              {validating && formData.updated_date === '' && (
                <div className='error_text'>
                  {'Please provide a valid date.'}
                </div>
              )}
            </div>
          </div>
          <div className='row mt-3 mb-2'>
            <div className='col-sm-12'>
              <p
                className='btn ubbe-blue-bg p-2 px-4 w-100 rounded-3 text-light d-flex m-auto justify-content-center align-items-center mb-2'
                style={{ maxWidth: 'max-content', height: '48px' }}
                onClick={() => {
                  handleAction();
                }}
              >
                Save
              </p>
              <u
                className='m-auto d-flex justify-content-center labelText'
                style={{ cursor: 'pointer' }}
                aria-label='Close'
                onClick={() => onClose()}
              >
                Cancel
              </u>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
